import React, { useEffect, useState } from 'react';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import {
    extensionStatuses,
    extractStatusText,
    getActiveUntil,
} from 'flow/utils';
import useAppContext from 'flow/hooks/useAppContext';
import {
    getExtensionKeyFromSession,
    getHasPresenceNotification,
} from 'flow/selectors/selectors';
import {
    createPresenceNotification,
    deletePresenceNotification,
} from 'flow/actions/extension-actions';
import { AppDispatch } from 'flow/types/Redux';
import { State } from 'flow/types/State';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { ChatSession } from 'flow/types/Chats';
import { twMerge } from 'tailwind-merge';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectExtension,
    selectExtensionActiveProfile,
    selectExtensionStatus,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';

interface Props {
    session: TypedMap<ChatSession>;
}

const ChatBanner = ({ session }: Props) => {
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();

    const extensionKey = getExtensionKeyFromSession(session, ownExtensionKey);
    const status = useAppSelector((state) =>
        selectExtensionStatus(state, extensionKey),
    );
    const bPartActiveProfile = useAppSelector((state) =>
        selectExtensionActiveProfile(state, extensionKey),
    );

    const hasPresenceNotification = useAppSelector((state) =>
        getHasPresenceNotification(state, ownExtensionKey, extensionKey),
    );

    const [showBanner, setShowBanner] = useState(false);
    useEffect(
        () => setShowBanner(status === extensionStatuses.unavailable.key),
        [session, status],
    );

    if (!showBanner) {
        return null;
    }

    return (
        <div className="flex justify-center">
            <div className="flex items-center gap-x-2 rounded-sm bg-gray-800 p-2 text-white">
                <IconButton
                    className={twMerge(
                        'size-7',
                        hasPresenceNotification
                            ? 'bg-green'
                            : 'bg-gray-600 hover:bg-gray-500',
                    )}
                    onClick={() =>
                        dispatch(
                            togglePresenceNotification(
                                extensionKey,
                                hasPresenceNotification,
                            ),
                        )
                    }
                    tooltipLabel={i18next.t(
                        'General.ExtensionBusyAlet.NotifyWhenAvailable',
                    )}
                >
                    <Icon
                        iconKey={
                            hasPresenceNotification
                                ? 'notifications_active'
                                : 'notifications'
                        }
                    />
                </IconButton>

                <p className="text-sm/4 font-bold">{`${extractStatusText(bPartActiveProfile, i18next)} ${getActiveUntil(bPartActiveProfile, i18next)}`}</p>

                <IconButton
                    aria-label={i18next.t('modal.close')}
                    className="ms-4 size-7"
                    onClick={() => setShowBanner(false)}
                >
                    <Icon iconKey="close" className="text-gray-400" />
                </IconButton>
            </div>
        </div>
    );
};

export default ChatBanner;

const togglePresenceNotification =
    (extensionKey: ExtensionKey, hasPresenceNotification: boolean) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const ownExtensionKey = selectOwnExtensionKey(getState());
        const extensionBPart = selectExtension(getState(), extensionKey);
        if (hasPresenceNotification) {
            dispatch(
                deletePresenceNotification(ownExtensionKey, extensionBPart),
            );
        } else {
            dispatch(
                createPresenceNotification(ownExtensionKey, extensionBPart),
            );
        }
    };
